"use client"

import React, { useMemo, useState } from 'react'

import { CollectionArchive } from '@/components/CollectionArchive'
import type { CardPostData } from '@/components/Card'

import type { NewsCategory } from './NewsHero'
import { NewsHero } from './NewsHero'

type NewsListingProps = {
  posts: CardPostData[]
  categories: NewsCategory[]
}

export const NewsListing: React.FC<NewsListingProps> = ({ posts, categories }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const normalizedSearch = searchTerm.trim().toLowerCase()

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesCategory =
        !selectedCategory ||
        (Array.isArray(post.categories) &&
          post.categories.some((category) => {
            if (!category || typeof category !== 'object') return false
            const { slug } = category as { slug?: unknown }
            return typeof slug === 'string' && slug === selectedCategory
          }))

      if (!matchesCategory) return false

      if (!normalizedSearch) return true

      const title = typeof post.title === 'string' ? post.title.toLowerCase() : ''
      const description =
        typeof post.meta === 'object' &&
        post.meta !== null &&
        typeof (post.meta as { description?: unknown }).description === 'string'
          ? ((post.meta as { description?: string }).description ?? '').toLowerCase()
          : ''

      return title.includes(normalizedSearch) || description.includes(normalizedSearch)
    })
  }, [normalizedSearch, posts, selectedCategory])

  return (
    <>
      <NewsHero
        categories={categories}
        selectedCategory={selectedCategory}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onSelectCategory={setSelectedCategory}
      />

      {filteredPosts.length > 0 ? (
        <CollectionArchive posts={filteredPosts} />
      ) : (
        <div className="container py-12 pb-130">
          <p className="text-xl text-mini-secondary">
            No news articles match your filters. Try adjusting your search or selecting a different category.
          </p>
        </div>
      )}
    </>
  )
}
